import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import NetworkIcon from '@/components/NetworkIcon';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { TestingChannelResource } from '@/types/resources';
import { api } from '@/utils/api';
import { numFormatter } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import TestingChannelFormModal from './TestingChannelFormModal';

const Description = styled.div`
  display: flex;
  align-items: center;
  svg {
    position: absolute;
    top: -2px;
    left: -2px;
  }
`;

const ImageWrap = styled.div`
  position: relative;
`;

export const Image = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

const Tr = styled.tr<{ $red: boolean }>`
  ${({ $red, theme }) =>
    $red &&
    css`
      > td {
        background: ${theme.colors.errorLight};
      }
    `}
`;

const DescriptionInner = styled.div`
  overflow-x: hidden;
  text-overflow: ellipsis;
  max-width: 132px;
  white-space: nowrap;
`;

interface Props {
  channel: TestingChannelResource;
}

const TestingSettingsTableRow: FC<Props> = ({ channel }) => {
  const [showEdit, setShowEdit] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const location = useLocation();

  const isYtOwner = useSelector(
    (rootState: RootState) =>
      process.env.REACT_APP_ENVIRONMENT === 'development' ||
      rootState.auth.user?.email === 'bpsmediaanalytics@boredpanda.com' ||
      rootState.auth.user?.email === 'loreta.z@boredpanda.com' ||
      rootState.auth.user?.id === 37,
  );

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        'Are you sure you want to delete this testing channel?',
        channel.name,
      );

      if (!confirmed) return;

      const { id } = await api.delete<never, { id: number }>(
        `/v1/testing-channels/${channel.id}`,
      );

      queryClient.setQueryData(
        ['testing_channels'],
        (channels: TestingChannelResource[] | undefined) =>
          channels!.filter((channel) => channel.id !== id),
      );

      alerts.success('Testing Channel was successfully deleted');
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Tr $red={!channel.authorized}>
      <Td>
        <Description>
          {!!channel.thumbnail && (
            <ImageWrap>
              <Image
                src={channel.thumbnail}
                alt={channel.name + ' thumbnail'}
              />
              <NetworkIcon type={channel.network} />
            </ImageWrap>
          )}
          <DescriptionInner>
            <Text $type="smallBold">{channel.name}</Text>
            <Text
              $type="extraSmall"
              $color="gray"
              as="a"
              href={channel.url}
              target="_blank"
            >
              {channel.url}
            </Text>
          </DescriptionInner>
        </Description>
      </Td>
      <Td>
        {channel.subscribers ? numFormatter(channel.subscribers, 1) : '-'}
      </Td>
      <Td>{channel.notes ?? '-'}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            {isYtOwner && (
              <ActionsDropdownButton
                href={`${process.env.REACT_APP_API_ENDPOINT}/socialite/${
                  channel.network === 'youtube' ? 'google' : 'facebook'
                }?testing_channel_id=${
                  channel.id
                }&redirect_path=${encodeURIComponent(location.pathname)}`}
                as="a"
              >
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={22}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                    />
                  </svg>
                </span>
                Authorize
              </ActionsDropdownButton>
            )}
            <ActionsDropdownButton
              onClick={() => setShowEdit(true)}
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEdit && (
            <TestingChannelFormModal
              onClose={() => setShowEdit(false)}
              channel={channel}
            />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default TestingSettingsTableRow;
